import { Clock, ArrowRight } from "lucide-react"
import { useNavigate } from "react-router-dom"
import type { Course } from "../types/api"
import iconSrc from "../../../../resources/icon.png"
import { appInfo } from "@renderer/config/appinfo"

interface Props {
  course: Course
  computedDuration?: string
  labProgress?: number
}

export const CourseCard = ({ course, computedDuration, labProgress }: Props) => {
  const navigate = useNavigate()

  const level =
    course.level?.toLowerCase() === "advanced"
      ? "Advanced"
      : course.level?.toLowerCase() === "intermediate"
      ? "Intermediate"
      : "Beginner"

  const isStarted = (labProgress ?? 0) > 0
  const goToCourse = () => navigate(`/course/${course.id}/modules`)

  return (
    <div
      className="group py-4 cursor-pointer border-b border-gray-200 last:border-b-0"
      onClick={goToCourse}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div
          className="relative w-full flex-shrink-0 overflow-hidden rounded-xl shadow-md sm:w-[34%]"
          style={{ aspectRatio: '16/10' }}
        >
          <img
            src={course.thumbnail ? `${appInfo.apiDomain}/storage/${course.thumbnail}` : iconSrc}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => { e.currentTarget.src = iconSrc }}
          />
          <span
            className="absolute right-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
            style={{ background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.28)' }}
          >
            {level}
          </span>
          {isStarted && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
              <div className="h-full bg-[#7a6020]" style={{ width: `${labProgress}%` }} />
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent px-3 pb-2.5 pt-8 sm:hidden">
            <h3 className="line-clamp-2 text-sm font-bold leading-snug text-white">
              {course.title}
            </h3>
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-1.5 sm:pl-1">
          <h3 className="hidden text-base font-bold leading-snug text-gray-900 transition-colors group-hover:text-[#7a6020] sm:block">
            {course.title}
          </h3>

          <p className="text-xs leading-relaxed text-gray-600 line-clamp-2">
            {course.description || 'Learn core concepts with real-world exercises and examples.'}
          </p>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {computedDuration || course.duration || '—'}
            </span>
            {isStarted && (
              <span className="font-semibold text-[#7a6020]">{labProgress}% done</span>
            )}
          </div>

          <div className="mt-1" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={goToCourse}
              className="flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:opacity-90"
              style={{ background: '#7a6020' }}
            >
              <ArrowRight className="w-3.5 h-3.5" />
              {isStarted ? 'Continue Learning' : 'Start Learning'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
